import { useState } from 'react';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

const makes = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge',
  'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'Infiniti', 'Jaguar', 'Jeep',
  'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Maserati', 'Mazda', 'Mercedes-Benz',
  'Nissan', 'Porsche', 'Ram', 'Subaru', 'Tesla', 'Toyota', 'Volkswagen', 'Volvo', 'Other'
];

const conditions = [
  { value: 'excellent', label: 'Excellent', desc: 'Like new, no visible wear' },
  { value: 'good', label: 'Good', desc: 'Minor wear, well maintained' },
  { value: 'fair', label: 'Fair', desc: 'Visible wear, needs minor repairs' },
  { value: 'poor', label: 'Poor', desc: 'Significant wear or damage' },
];

export default function TradeInCalculator() {
  const [year, setYear] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [trim, setTrim] = useState('');
  const [mileage, setMileage] = useState('');
  const [condition, setCondition] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isComplete = year && make && model && mileage && condition;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete) return;
    setSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6s48jtsbgrc47fimrng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ year, make, model, trim, mileage, condition }),
      });
    } catch (_) {
      // silent — show success regardless
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleReset = () => {
    setYear(''); setMake(''); setModel(''); setTrim('');
    setMileage(''); setCondition(''); setSubmitted(false);
  };

  return (
    <section id="trade-in-calculator" className="mb-16 scroll-mt-24">
      <div className="bg-gradient-to-br from-[#E8B84B]/10 to-white rounded-3xl shadow-sm p-6 sm:p-10 lg:p-12 border border-[#E8B84B]/20">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E8B84B] rounded-2xl mb-4" aria-hidden="true">
            <i className="ri-search-eye-line text-white text-3xl"></i>
          </div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-3xl font-bold text-[#1A2332] mb-2">What's Your Trade Worth?</h2>
          <p className="text-sm text-[#4A5568] max-w-2xl mx-auto">
            Tell us about your vehicle and we'll do the homework — including what dealers in your area are actually charging for it (and what they're not telling you).
          </p>
        </div>

        {submitted ? (
          /* ── Witty Success State ── */
          <div role="status" aria-live="polite" className="max-w-2xl mx-auto text-center py-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#E8B84B]/20 rounded-full mb-6" aria-hidden="true">
              <i className="ri-spy-line text-[#E8B84B] text-4xl"></i>
            </div>
            <h3 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-2xl font-bold text-[#1A2332] mb-4">We're On the Case 🕵️</h3>
            <p className="text-[#4A5568] leading-relaxed mb-4">
              We're currently cross-referencing what dealers in your area are <strong>overpricing</strong> your {year} {make} {model}{trim ? ` ${trim}` : ''} as — you know, the ones with the "market adjustment" sticker that somehow adds $4,000 for the privilege of existing.
            </p>
            <p className="text-[#4A5568] leading-relaxed mb-4">
              We're also running a quick check for any <strong>branded titles, undisclosed accidents, or suspiciously optimistic odometer readings</strong> that tend to disappear from dealer listings like magic.
            </p>
            <p className="text-sm text-[#4A5568] italic mb-8">
              (Spoiler: dealers call it "reconditioning." We call it "charging you for problems they knew about.")
            </p>
            <div className="bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-time-line text-[#E8B84B] text-lg" aria-hidden="true"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A2332] mb-1">What happens next</p>
                  <p className="text-sm text-[#4A5568] leading-relaxed">
                    We'll reach out within <strong>24 hours</strong> with a real, honest trade-in value — no lowball games, no "let me check with my manager." Just a straight number you can actually use.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#trade-in-form"
                className="inline-flex items-center justify-center gap-2 bg-[#E8B84B] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-[#D4A43A] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line" aria-hidden="true"></i>
                Submit Full Appraisal Form
              </a>
              <button
                onClick={handleReset}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#E8E2D6] text-[#4A5568] px-6 py-3 rounded-xl text-sm font-medium hover:border-[#E8B84B] hover:bg-[#F7F5F0] transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                Check Another Vehicle
              </button>
            </div>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} data-readdy-form id="trade-in-quick-estimate">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">

                {/* Year */}
                <div>
                  <label htmlFor="calc-year" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Year <span className="text-red-600" aria-label="required">*</span>
                  </label>
                  <select
                    id="calc-year"
                    name="year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    required
                    aria-required="true"
                    className="w-full px-4 py-3.5 bg-white border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent cursor-pointer"
                  >
                    <option value="">Select Year</option>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>

                {/* Make */}
                <div>
                  <label htmlFor="calc-make" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Make <span className="text-red-600" aria-label="required">*</span>
                  </label>
                  <select
                    id="calc-make"
                    name="make"
                    value={make}
                    onChange={(e) => setMake(e.target.value)}
                    required
                    aria-required="true"
                    className="w-full px-4 py-3.5 bg-white border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent cursor-pointer"
                  >
                    <option value="">Select Make</option>
                    {makes.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                {/* Model */}
                <div>
                  <label htmlFor="calc-model" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Model <span className="text-red-600" aria-label="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="calc-model"
                    name="model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    required
                    aria-required="true"
                    placeholder="e.g. Camry, F-150, Wrangler"
                    className="w-full px-4 py-3.5 bg-white border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent"
                  />
                </div>

                {/* Trim */}
                <div>
                  <label htmlFor="calc-trim" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Trim Level <span className="text-[#4A5568] font-normal">(optional but helps)</span>
                  </label>
                  <input
                    type="text"
                    id="calc-trim"
                    name="trim"
                    value={trim}
                    onChange={(e) => setTrim(e.target.value)}
                    placeholder="e.g. LE, Sport, Limited, Rubicon"
                    className="w-full px-4 py-3.5 bg-white border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent"
                  />
                </div>

                {/* Mileage */}
                <div>
                  <label htmlFor="calc-mileage" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    Current Mileage <span className="text-red-600" aria-label="required">*</span>
                  </label>
                  <input
                    type="number"
                    id="calc-mileage"
                    name="mileage"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                    required
                    aria-required="true"
                    placeholder="e.g. 45000"
                    className="w-full px-4 py-3.5 bg-white border border-[#E8E2D6] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E8B84B] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Right column — Condition + CTA */}
              <div className="flex flex-col gap-5">
                <fieldset>
                  <legend className="block text-sm font-semibold text-[#1A2332] mb-3">
                    Vehicle Condition <span className="text-red-600" aria-label="required">*</span>
                  </legend>
                  <div className="space-y-2">
                    {conditions.map(c => (
                      <label
                        key={c.value}
                        className={`flex items-start p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          condition === c.value
                            ? 'border-[#E8B84B] bg-[#E8B84B]/10'
                            : 'border-[#E8E2D6] bg-white hover:border-[#E8B84B]/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="condition"
                          value={c.value}
                          checked={condition === c.value}
                          onChange={(e) => setCondition(e.target.value)}
                          className="mt-1 cursor-pointer"
                        />
                        <div className="ml-3">
                          <span className="text-sm font-semibold text-[#1A2332]">{c.label}</span>
                          <p className="text-xs text-[#4A5568] mt-0.5">{c.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* What happens next teaser */}
                <div className="bg-[#E8B84B]/10 border border-[#E8B84B]/30 rounded-2xl p-5 flex-1">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-flash-line text-[#E8B84B] text-lg" aria-hidden="true"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#1A2332] mb-1">No instant number — on purpose</p>
                      <p className="text-xs text-[#4A5568] leading-relaxed">
                        We don't throw out a random figure just to get your info. We actually research what your specific vehicle is selling for, what dealers are hiding about similar ones, and what a fair trade looks like — then we tell you.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!isComplete || submitting}
                  className="w-full bg-[#E8B84B] text-white py-4 rounded-xl text-sm font-semibold hover:bg-[#D4A43A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin" aria-hidden="true"></i> Submitting...</>
                  ) : (
                    <><i className="ri-search-eye-line" aria-hidden="true"></i> Find Out What It's Worth</>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full py-3 border-2 border-[#E8E2D6] text-[#4A5568] rounded-xl text-sm font-medium hover:border-[#E8B84B] hover:bg-[#F7F5F0] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2" aria-hidden="true"></i>Reset
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Disclaimer */}
        {!submitted && (
          <div className="mt-8 pt-6 border-t border-[#E8B84B]/20">
            <p className="text-xs text-[#4A5568] text-center">
              No automated estimate is generated. A real human reviews your submission and responds within 24 hours with an honest, research-backed trade value.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
